/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {UiButtonStyle} from 'types/enums/UiButtonStyle'
import {UiButtonType} from 'types/enums/UiButtonType'
import {UiButtonSize} from 'types/enums/UiButtonSize'

export { UiButtonStyle, UiButtonType, UiButtonSize }

interface UiButtonProps {
    icon?: React.ReactNode | React.ReactNode[]
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
    title?: string
    type?: UiButtonType
    style?: UiButtonStyle
    size?: UiButtonSize
    disabled?: boolean
    children?: React.ReactNode | React.ReactNode[]
    data?: unknown
    id?: string
    fullWidth?: boolean
    colSpan?: string
    className?: string
    loading?: boolean
    dataTooltipId?: string
    dataTooltipContent?: string
}

const styleClasses: Record<UiButtonStyle, string> = {
    [UiButtonStyle.Add]:   'UiButtonStyle.Add text-white bg-cta-light hover:bg-cta-dark border-cta-light hover:border-cta-dark',
    [UiButtonStyle.Amber]: 'UiButtonStyle.Amber bg-amber-200 dark:bg-amber-900 hover:bg-amber-300 dark:hover:bg-amber-800 ',
    [UiButtonStyle.Blue]:  'UiButtonStyle.Blue bg-blue-400 hover:bg-blue-500 dark:bg-blue-800 text-blue-900 dark:text-blue-200 border-transparent',
    [UiButtonStyle.CLOSE]: 'UiButtonStyle.CLOSE bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
    [UiButtonStyle.Cyan]:  'UiButtonStyle.Cyan text-cyan-900 dark:text-cyan-300 dark:bg-cyan-700 bg-cyan-500 hover:bg-cyan-600 border-transparent',
    [UiButtonStyle.DELETE]:'UiButtonStyle.DELETE text-white bg-red-500 hover:bg-red-600 border-transparent',
    [UiButtonStyle.Dark]:  'UiButtonStyle.Dark text-white bg-gray-700 hover:bg-gray-900 border-transparent',
    [UiButtonStyle.Disabled]: 'UiButtonStyle.Disabled opacity-50 cursor-not-allowed',
    [UiButtonStyle.EDIT]: 'UiButtonStyle.EDIT text-white bg-cta-light hover:bg-cta-dark border-cta-light hover:border-cta-dark',
    [UiButtonStyle.Gray]: 'UiButtonStyle.Gray text-white bg-gray-600 hover:bg-gray-700 border-transparent',
    [UiButtonStyle.GradientGreen]: 'UiButtonStyle.GradientGreen bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-sm shadow-green-200 dark:shadow-green-900',
    [UiButtonStyle.Green]: 'UiButtonStyle.Green text-white bg-green-500 hover:bg-green-600 border-transparent',
    [UiButtonStyle.INFO]: 'UiButtonStyle.INFO text-white bg-blue-400 hover:bg-blue-500 text-white border-transparent',
    [UiButtonStyle.Indigo]:       'UiButtonStyle.Indigo bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/10 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-900/20',
    [UiButtonStyle.LIGHT]:        'UiButtonStyle.LIGHT bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
    [UiButtonStyle.Pagination]:        'UiButtonStyle.Pagination bg-white text-gray-700 border border-gray-300 hover:text-white hover:bg-blue-500 hover:border-blue-500 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-blue-500 dark:hover:border-blue-500',
    [UiButtonStyle.Primary]:         'UiButtonStyle.Primary bg-cta-light dark:bg-cta-light dark:hover:bg-cta-dark text-white hover:bg-cta-dark focus:border-cta-light hover:shadow-lg hover:-translate-y-0.5 border border-transparent',
    [UiButtonStyle.Print]: 'UiButtonStyle.Print text-white bg-blue-600 hover:bg-blue-700 border-transparent',
    [UiButtonStyle.Red]: 'UiButtonStyle.Red text-white bg-red-500 hover:bg-red-600 border-transparent',
    [UiButtonStyle.SAVE]: 'UiButtonStyle.SAVE text-white bg-green-500 hover:bg-green-600 border-transparent',
    [UiButtonStyle.Secondary]:         'UiButtonStyle.Secondary bg-white text-cta-light border border-cta-light hover:bg-cta-50 dark:bg-gray-900 dark:text-cta-light dark:border-cta-light dark:hover:bg-cta-dark',
    [UiButtonStyle.White]:     'UiButtonStyle.White bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
    [UiButtonStyle.Whitesmoke]:     'UiButtonStyle.Whitesmoke bg-gray-200 text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
    [UiButtonStyle.Yellow]: 'UiButtonStyle.Yellow bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-800 dark:hover:bg-yellow-700 text-yellow-700 dark:text-yellow-300',
}

const sizeClasses: Record<UiButtonSize, string> = {
    [UiButtonSize.Small]: 'UiButtonSize.Small px-2 py-1 text-xs font-semibold',
    [UiButtonSize.Normal]: 'UiButtonSize.Normal px-4 py-2 text-sm font-semibold',
    [UiButtonSize.Medium]: 'UiButtonSize.Medium px-6 py-2 text-base font-medium',
    [UiButtonSize.Large]: 'UiButtonSize.Large px-10 py-3 text-lg font-bold',
    [UiButtonSize.Pagination]: 'UiButtonSize.Pagination TableFoot-control min-w-9 justify-center px-2.5 rounded-lg border shadow-sm',
}

const UiButton: React.FC<UiButtonProps> = (props) => {
    const { t } = useTranslation()
    const classElement =
        'inline-flex items-center justify-center rounded-lg border  transition-all duration-200 shadow-md focus:outline-none focus:border-primary-600 transform cursor-pointer'

    const styleClass = styleClasses[props.style || UiButtonStyle.Primary]
    const sizeClass = sizeClasses[props.size || UiButtonSize.Medium]

    const isDisabled = props.disabled || props.loading
    const ariaLabel = props.title && !props.children ? t(props.title) : undefined
    const buttonType = props.type || UiButtonType.Button
    const hasLeadingVisual = Boolean(props.loading || props.icon)
    const hasLabel = Boolean(props.children || props.title)
    const iconLabelGap =
        props.size === UiButtonSize.Small
            ? 'gap-2'
            : props.size === UiButtonSize.Large
              ? 'gap-2.5'
              : 'gap-2'

    return (
        <button
            className={clsx(
                'UiButton',
                classElement,
                props.fullWidth && 'w-full',
                props.colSpan && props.colSpan,
                sizeClass,
                styleClass,
                hasLeadingVisual && hasLabel && iconLabelGap,
                props.disabled &&
                'bg-slate-300 cursor-not-allowed! hover:transform-none hover:shadow-md',
                props.className
            )}
            id={props.id}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            data-tooltip-id={props.dataTooltipId}
            data-tooltip-content={props.dataTooltipContent}
            aria-label={ariaLabel}
            type={buttonType}
            onClick={props.onClick}
        >
            {props.loading && (
                <svg
                    className="animate-spin h-4 w-4 text-current shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    ></circle>
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
            )}
            {props.icon && <span className="inline-flex shrink-0">{props.icon}</span>}
            {props.title && <span>{t(props.title)}</span>}
            {props.children}
        </button>
    )
}

export default UiButton
